import React from "react";
import { Sun, Moon } from "lucide-react";

const THEME_KEY = "orbit-theme";
type Theme = "light" | "dark";

function currentTheme(): Theme {
  const attr = document.documentElement.getAttribute("data-theme");
  return attr === "light" ? "light" : "dark";
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute("data-theme", theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", theme === "light" ? "#ffffff" : "#06060f");
}

/**
 * Theme toggle — flips between light/dark, persists the choice, and keeps
 * the document attribute in sync. The initial theme is set pre-paint by the
 * inline bootstrap in index.html, so this only handles user toggles and
 * follows the OS preference until the user makes an explicit choice.
 */
export function ThemeToggle() {
  const [theme, setTheme] = React.useState<Theme>(() => currentTheme());

  // Follow system preference changes — but only while the user hasn't chosen.
  React.useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem(THEME_KEY)) return;
      const next: Theme = e.matches ? "light" : "dark";
      applyTheme(next);
      setTheme(next);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  function toggle() {
    const next: Theme = theme === "light" ? "dark" : "light";
    applyTheme(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      /* ignore storage failures (private mode etc.) */
    }
    setTheme(next);
  }

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={toggle}
      aria-label={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
      title={theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
    >
      <Sun size={16} className="icon-sun" aria-hidden />
      <Moon size={16} className="icon-moon" aria-hidden />
    </button>
  );
}
